import type { COMPONENT_METRICS } from '../utils/analysis-helpers';

/**
 * Default switches of the CRM delivery "actions" (the non-field part of
 * `generic_field_config`).
 *
 * ONE map read by both sides: edge-api seeds a new organization's settings with
 * it (`DEFAULT_MULTI_PROVIDER_CONFIG`), and workflows-api falls back to it when
 * an organization has no stored `generic_field_config`. Two copies drifted once:
 * the seed said the timeline note is off (and the docs say so) while delivery
 * wrote it whenever the config was absent (#1686).
 *
 * `DEFAULT_GENERIC_FIELD_CONFIG` is the whole `generic_field_config` seed and
 * `withGenericFieldConfigDefaults` the one way a STORED config is completed
 * with it — edge-api (screens, PATCH) and workflows-api (delivery) both go
 * through it. A stored config that predates a key, or that a link flow patched
 * with two action flags, is partial; parsing it strictly dropped the whole
 * config and with it the flags it did carry (#1686 review).
 */
export const CRM_DELIVERY_ACTION_DEFAULTS = {
  attachTranscription: true,
  addTimelineComment: true,
  addTimelineNote: false,
  autoFillEmptyFields: false,
  createActivitiesFromAgreements: false,
} as const satisfies Record<string, boolean>;

/** The Aelo fields and actions an organization's delivery config switches. */
export const DEFAULT_GENERIC_FIELD_CONFIG = {
  // Basic fields. `transcript` is OFF, unlike its neighbours: it writes the full
  // conversation text into a card field, and everyone must not get that at once —
  // the customer turns it on together with the target field (#316). The full
  // transcript reaches the CRM anyway, attached to the call card.
  transcript: false,
  summary: true,
  qualityScore: true,
  saleProbability: true,
  keywords: false,
  sentiment: false,

  // Quality score breakdown (off: most users do not need granular scores)
  dialogueTechnique: false,
  contentQuality: false,
  emotionalIntelligence: false,
  complianceScore: false,
  speechQuality: false,

  // Dialogue metrics
  needsQualification: false,
  objectionHandling: false,
  closingTechnique: false,

  // Content metrics
  relevance: false,
  completeness: false,
  accuracy: false,

  // Emotional metrics
  empathy: false,
  rapport: false,
  sentimentDelta: false,

  // Compliance metrics
  recordingDisclaimer: false,
  tcpaCompliance: false,
  dataPrivacy: false,

  // Speech metrics
  clarity: false,
  professionalism: false,
  fillerWords: false,

  // Actions and CRM delivery enrichment
  ...CRM_DELIVERY_ACTION_DEFAULTS,
} as const satisfies Record<string, boolean>;

/**
 * A provider's stored `field_mappings` with a PATCH's keys laid over it
 * (#3001): a key the patch lacks stays, `null` removes the key («Aelo default
 * field» — delivery falls back to `UF_CRM_AICA_…`), a string sets it. The
 * server stores by it and the settings step mirrors the save with it, so the
 * two cannot drift.
 */
export function mergeFieldMappingsPatch<V>(
  stored: Readonly<Record<string, V>>,
  patch: Readonly<Record<string, string | null>> | undefined
): Record<string, V | string> {
  const merged: Record<string, V | string> = { ...stored };
  for (const [key, fieldId] of Object.entries(patch ?? {})) {
    if (fieldId === null) delete merged[key];
    else merged[key] = fieldId;
  }
  return merged;
}

/**
 * A stored `generic_field_config` completed with the defaults: what the
 * organization stored wins, every key it does not carry takes its default. Not an
 * object (absent, null, a string) counts as nothing stored.
 */
export function withGenericFieldConfigDefaults(stored: unknown): Record<string, unknown> {
  const base =
    typeof stored === 'object' && stored !== null && !Array.isArray(stored) ? stored : {};
  return { ...DEFAULT_GENERIC_FIELD_CONFIG, ...base };
}

/**
 * The results delivery writes into a CRM card field — the ONLY keys of
 * `generic_field_config` that reach a field. The other eleven result flags
 * (`keywords`, `closingTechnique`, `relevance`…) stay in the schema and in stored
 * settings but are never written: the settings screen offers only these rows
 * (#2939, spec #2979), and workflows-api builds `fieldMappings` from the same
 * list. One list read by both sides, so the screen cannot offer a row delivery
 * drops without a word.
 *
 * Order is the screen's order: conversation outcome, skill scores (0–100),
 * compliance (yes / no).
 */
export const CRM_WRITABLE_FIELD_KEYS = [
  // Written only when the customer turns it on: the default is `false` (#316).
  'transcript',
  'summary',
  'qualityScore',
  'saleProbability',
  'sentiment',
  'dialogueTechnique',
  'contentQuality',
  'emotionalIntelligence',
  'complianceScore',
  'speechQuality',
  'needsQualification',
  'objectionHandling',
  // Built-in criterion answers (#2860): CRM card only — a smart process has no
  // field for them, and delivery filters the target out.
  'recordingDisclaimer',
  'tcpaCompliance',
  'dataPrivacy',
] as const;

export type CrmWritableFieldKey = (typeof CRM_WRITABLE_FIELD_KEYS)[number];

/**
 * The writable results that have a field on a smart-process item — the Aelo
 * field created with the smart process (`ufCrm<typeId>Aica…`, the suffixes in
 * `GENERIC_TO_SP_SUFFIX`, @aica/connectors, which is typed against this list).
 * Delivery to a smart process writes these by the same `generic_field_config`
 * flags; the field cannot be chosen. The seven score keys name a slot, not
 * their own value — see `CRM_SCORE_FLAG_METRICS`. The transcript and the three compliance
 * answers have no smart-process field (the transcript goes to the item's
 * timeline instead). The settings step shows exactly these rows for a smart
 * process (#3002, owner's decision 01.11).
 */
export const CRM_SMART_PROCESS_FIELD_KEYS = [
  'summary',
  'qualityScore',
  'saleProbability',
  'sentiment',
  'dialogueTechnique',
  'contentQuality',
  'emotionalIntelligence',
  'complianceScore',
  'speechQuality',
  'needsQualification',
  'objectionHandling',
] as const satisfies readonly CrmWritableFieldKey[];

export type CrmSmartProcessFieldKey = (typeof CRM_SMART_PROCESS_FIELD_KEYS)[number];

/**
 * What each of the seven score switches writes, for BOTH destinations — the
 * bound CRM card and a smart-process item (#3029, owner's decision 01.11).
 * The switches are named after results (dialogueTechnique, …) the analysis
 * does not produce; delivery writes the record's component scores instead
 * (greetingScore → dialogueTechnique, …, crmScore → objectionHandling — the
 * legacy A–G order of `qa-metrics.ts`, which also names the smart-process
 * slots `GENERIC_TO_SP_SUFFIX`: dialogueTechnique → AICA_SCORE_A, …).
 *
 * The one place the switch → metric rule lives: delivery reads the metric
 * from here (`getDeliveryTargets`, workflows-api), and the settings step labels
 * both field tables by the metric, as the record page names it, so a switch
 * says what it actually writes (#3002).
 */
export const CRM_SCORE_FLAG_METRICS = {
  dialogueTechnique: 'greetingScore',
  contentQuality: 'needsScore',
  emotionalIntelligence: 'objectionScore',
  complianceScore: 'closingScore',
  speechQuality: 'politenessScore',
  needsQualification: 'clarityScore',
  objectionHandling: 'crmScore',
} as const satisfies Partial<
  Record<CrmSmartProcessFieldKey, (typeof COMPONENT_METRICS)[number]>
>;

/** Where CRM delivery writes: the bound CRM card or a smart-process item. */
export type CrmDeliveryDestination = 'crm_card' | 'smart_process';

/**
 * The destination delivery actually uses for a provider `config`: an explicit
 * `destination` wins; without one, the legacy pair `smartProcessEnabled` +
 * `smartProcessId` means a smart process; anything else is the CRM card.
 * `crmCardEnabled` is NOT read — delivery never read it, and a screen that
 * decides by it would show a field table delivery no longer uses (spec #2979, §4).
 *
 * Read by workflows-api (target resolution) and the settings screen (whether
 * the field table applies). Whether the vendor supports smart processes at all
 * is the caller's concern (`getCrmDeliverySupport`).
 */
export function resolveCrmDeliveryDestination(
  config: Readonly<Record<string, unknown>> | null | undefined
): CrmDeliveryDestination {
  if (config?.destination === 'smart_process') return 'smart_process';
  if (config?.destination === 'crm_card') return 'crm_card';
  return Boolean(config?.smartProcessEnabled) && Boolean(config?.smartProcessId)
    ? 'smart_process'
    : 'crm_card';
}
